import {createContext, useContext} from 'react'

import {CellValidationContextProvider} from '../../../components/table/cell-validation'
import {
  RowMenuShortcutContext,
  RowMenuShortcutProvider,
  type RowMenuShortcutState,
} from '../../../components/table/state-providers/row-menu-shortcut-provider'
import type {MemexItemModel} from '../../../models/memex-item-model'
import type {Table, TableState} from '../helpers/types'
import {ColumnOrderDndProvider} from './columns/column-order-dnd-provider'

export type TableStateSelectedRowIdsContextValue = TableState['rowSelection']
type TableStateColumnResizingContextValue = TableState['columnSizing']
export type TableContextValue = Table<MemexItemModel>

export const TableStateSelectedRowIdsContext = createContext<TableStateSelectedRowIdsContextValue | null>(null)
const TableStateColumnResizingContext = createContext<TableStateColumnResizingContextValue | null>(null)
export const TableContext = createContext<TableContextValue | null>(null)

/**
 * Sets up a context in which consumers can access the React Table instance
 *
 * @param props Props with a `table` attribute
 */
export const TableProvider: React.FC<{
  table: Table<MemexItemModel>
  children: React.ReactNode
}> = props => {
  const state = props.table.getState()
  return (
    <ColumnOrderDndProvider>
      <TableStateSelectedRowIdsContext value={state.rowSelection}>
        <RowMenuShortcutProvider>
          <TableStateColumnResizingContext value={state.columnSizing}>
            <TableContext value={props.table}>
              <CellValidationContextProvider>{props.children}</CellValidationContextProvider>
            </TableContext>
          </TableStateColumnResizingContext>
        </RowMenuShortcutProvider>
      </TableStateSelectedRowIdsContext>
    </ColumnOrderDndProvider>
  )
}

/**
 * Get a reference to the v8 table instance object.
 *
 * @link [V8 Table API docs](https://tanstack.com/table/v8/docs/api/core/table)
 *
 * Caution: Be careful with this hook—our table instance never changes, so table
 * state updates will not cause consumers of this hook to re-render, for
 * example.
 */
export function useTableInstance(): Table<MemexItemModel> {
  const table = useContext(TableContext)

  if (!table) {
    throw new Error('Must use `useTableInstance` in child of `<TableProvider>`')
  }

  return table
}

/**
 * Get the table's row menu shortcut origin.
 */
export function useTableRowMenuShortcutOrigin(): RowMenuShortcutState['rowMenuShortcutOrigin'] {
  const rowMenuContext = useContext(RowMenuShortcutContext)
  return rowMenuContext?.rowMenuState?.rowMenuShortcutOrigin
}

/**
 * Get the table's internal column resizing state.
 */
export function useTableColumnResizing(): TableStateColumnResizingContextValue {
  const columnResizing = useContext(TableStateColumnResizingContext)

  if (!columnResizing) {
    throw new Error('Must use `useTableColumnResizing` in child of `<TableProvider>')
  }

  return columnResizing
}

try{ TableStateSelectedRowIdsContext.displayName ||= 'TableStateSelectedRowIdsContext' } catch {}
try{ TableStateColumnResizingContext.displayName ||= 'TableStateColumnResizingContext' } catch {}
try{ TableContext.displayName ||= 'TableContext' } catch {}